import React, { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";

/**
 * SplineRobot: wraps the <spline-viewer> web component.
 * - Hides the Spline watermark via shadowRoot styling.
 * - Fades in once the scene is ready.
 * - Whole canvas is clickable when `onClick` is provided.
 * - Optional `glow` prop tints the halo color (e.g. while listening).
 */
export default React.memo(function SplineRobot({
  url = "https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode",
  className = "",
  testId = "spline-robot",
  onClick,
  glow = "amber", // "amber" | "info" | "success"
  ariaLabel = "Talk to Nayan",
}) {
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    let viewer;
    let cancelled = false;

    const create = () => {
      if (cancelled) return;
      // Ensure the container has dimensions to avoid WebGL 0x0 errors
      if (node.clientWidth === 0 || node.clientHeight === 0) {
        // Wait up to 2 seconds for layout, then proceed anyway
        if (!node._started) node._started = Date.now();
        if (Date.now() - node._started < 2000) {
          setTimeout(create, 100);
          return;
        }
      }
      viewer = document.createElement("spline-viewer");
      viewer.setAttribute("url", url);
      viewer.setAttribute("loading-anim-type", "none");
      viewer.style.width = "100%";
      viewer.style.height = "100%";
      viewer.style.background = "transparent";
      viewer.style.display = "block";
      viewer.style.opacity = "0";
      viewer.style.transition = "opacity 1.4s cubic-bezier(0.2,0.8,0.2,1)";
      viewer.style.pointerEvents = "auto";

      const onReady = () => {
        try {
          const shadow = viewer.shadowRoot;
          if (shadow) {
            const style = document.createElement("style");
            style.textContent = `
              #logo, a[href*="spline"], #spline-logo, .spline-watermark { display: none !important; opacity: 0 !important; }
            `;
            shadow.appendChild(style);
            const logo = shadow.getElementById("logo");
            if (logo) logo.style.display = "none";
          }
        } catch {}
        viewer.style.opacity = "1";
        setReady(true);
      };

      viewer.addEventListener("load", onReady);
      viewer.addEventListener("load-complete", onReady);
      setTimeout(onReady, 3500);

      node.appendChild(viewer);
    };

    if (window.customElements && window.customElements.get("spline-viewer")) {
      create();
    } else {
      window.customElements?.whenDefined("spline-viewer").then(create).catch(() => {});
      const t = setTimeout(create, 1500);
      return () => {
        cancelled = true;
        clearTimeout(t);
        if (viewer && node.contains(viewer)) node.removeChild(viewer);
      };
    }
    return () => {
      cancelled = true;
      if (viewer && node && node.contains(viewer)) node.removeChild(viewer);
    };
  }, [url]);

  const glowMap = {
    amber: "rgba(245, 176, 65, 0.18)",
    info: "rgba(143, 184, 224, 0.22)",
    success: "rgba(125, 211, 160, 0.22)",
  };
  const glowColor = glowMap[glow] || glowMap.amber;

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? ariaLabel : undefined}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={`relative ${onClick ? "cursor-pointer focus:outline-none" : ""} ${className}`}
      style={{
        // keep WebGL canvas above any halo divs we might place behind
      }}
    >
      {/* Glow background — tints with phase */}
      <div
        className="absolute inset-x-12 top-12 bottom-12 rounded-full pointer-events-none transition-colors duration-700 blur-3xl"
        style={{ backgroundColor: glowColor }}
      />

      {/* Placeholder while loading */}
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-32 h-32">
            <div className="absolute inset-0 rounded-full border border-brand/20 pulse-soft" />
            <div className="absolute inset-3 rounded-full border border-brand/15 pulse-soft" style={{ animationDelay: "0.4s" }} />
            <div className="absolute inset-6 rounded-full border border-brand/10 pulse-soft" style={{ animationDelay: "0.8s" }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="text-brand/60 spin-slow" size={24} />
            </div>
          </div>
          <div className="absolute bottom-12 inset-x-0 text-center text-xs text-zinc-600">
            Booting up Nayan…
          </div>
        </div>
      )}
    </div>
  );
});
